import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import Input from '~/bootcamp/Activities/components/Input'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { NumHistory } from '~/bootcamp/Activities/Multiplication/MultiBy/types'
import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import Equals from '~/bootcamp/Activities/components/Equals'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { difficulty } from './constents'

export default function MultiByTwoDigit({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<NumHistory[]>([])
  const historyNumbers = useMemo(() => taskHistory.map(({ num }) => num), [taskHistory])
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('generics')
  useFocus(autofocus, answerState, inputRef)

  let minNumber = 11
  let maxNumber = 25

  if (difficultyLevel) {
    minNumber = difficulty(difficultyLevel)[0]
    maxNumber = difficulty(difficultyLevel)[1]
  }

  const doubleDigits = useMemo(() => {
    return generateDigits(minNumber, maxNumber, historyNumbers)
  }, [historyNumbers, minNumber, maxNumber])

  const singleDigits = useMemo(() => {
    if (difficultyLevel) return generateDigits(2, difficultyLevel === 1 ? 6 : 9, historyNumbers)
  }, [historyNumbers, difficultyLevel])

  useEmitInstance(
    onInstanceChange,
    doubleDigits && singleDigits ? `${doubleDigits} \\cdot ${singleDigits} = \\square` : null,
  )

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = Boolean(singleDigits && parseFloat(answer) === doubleDigits * singleDigits)
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setAnswer('')
    const num = doubleDigits
    setTaskHistory((prevState) => {
      return [{ num }, ...prevState].slice(0, 3)
    })
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="py-5">
      <MultiplicationActivityWrapper>
        <div>
          <span>{doubleDigits}</span>
          <Times />
          <span>{singleDigits}</span>
        </div>
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={15}
            onChange={(value) => {
              setAnswer(value)
              onAnswerStateChange(null)
            }}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function generateDigits(min: number, max: number, historyNumbers: number[]): number {
  const num = Math.floor(Math.random() * (max - min + 1) + min)

  if (historyNumbers.includes(num)) {
    return generateDigits(min, max, historyNumbers)
  }

  return num
}
